import { memo } from "react";
import { Card } from "react-bootstrap";
import { Moment } from "moment";

import { ReactComponent as GraphArrowIncrease } from "assets/svg/graph-arrow-increase.svg";
import { ReactComponent as GraphArrowDecrease } from "assets/svg/graph-arrow-decrease.svg";

import { DATE_TIME_FORMATS } from "constant";
import {
    ReportIndividualData,
    ReportIndividualEdgeItem,
    ReportItemSummaryType,
    ReportTimeRangeType,
} from "types/Reports";
import { isReportIndividualDatasValid } from "./utils/report";
import { getLowestHighestAndSortIndividualData } from "./utils/reportArray";
import {
    formatDateInReport,
    formatValueAndUnitBasedOnTypeInReport,
} from "./utils/reportFormat";
import {
    getHighestOrLowestNameDisplay,
    getIndividualPeakDescriptions,
    getTitleByTime,
} from "./utils/reportString";
import ReportLoadingChart from "./ReportLoadingChart";
import ReportEmptyChart from "./ReportEmptyChart";
import ReportTable from "./ReportTable";

type ReportIndividualUsageCardProps = {
    title: string;
    itemType: ReportItemSummaryType;
    timeRangeType: ReportTimeRangeType;
    isLoading: boolean;

    headers: string[];
    data: ReportIndividualData[];

    usageName: string;
    unit: string;

    colorClassName: string;
    selectedMonth: Moment
    dateFormat: string;
};

const getPeakDisplays = (
    itemType: ReportItemSummaryType,
    unit: string,
    highest: ReportIndividualEdgeItem,
    lowest: ReportIndividualEdgeItem,
    inMonthDate: string,
    usageName: string,
) => {
    const highestValueDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, highest.value, unit, true);
    const lowestValueDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, lowest.value, unit, true);

    const { highest: highestDescription, lowest: lowestDescription } =
        getIndividualPeakDescriptions(itemType, inMonthDate, usageName);

    return {
        highestValueDisplay,
        lowestValueDisplay,
        highestDescription,
        lowestDescription,
    };
};

export default memo(function ReportIndividualUsageCard({
    title,
    itemType,
    timeRangeType,
    isLoading,

    headers,
    data,

    usageName,

    unit,

    colorClassName,
    selectedMonth,
    dateFormat,
}: Readonly<ReportIndividualUsageCardProps>) {
    const isValid = isReportIndividualDatasValid(data, timeRangeType);
    let body = isLoading ? <ReportLoadingChart /> : <ReportEmptyChart />;

    if (isValid && !isLoading) {
        const { highest, lowest, sortedData } = getLowestHighestAndSortIndividualData(timeRangeType, data);

        const maxValue = highest.value;
        const minValue = lowest.value;

        const chart = <ReportTable
            headers={headers}
            itemType={itemType}
            data={sortedData}
            unit={unit}
            colorClassName={colorClassName}
            dateFormat={dateFormat}
            maxValue={maxValue}
            minValue={minValue}
        />;

        const {
            highestValueDisplay,
            lowestValueDisplay,
            highestDescription,
            lowestDescription,
        } = getPeakDisplays(
            itemType,
            unit,
            highest,
            lowest,
            formatDateInReport(selectedMonth, DATE_TIME_FORMATS.MONTH_ONLY),
            usageName,
        );

        body = <>
            <section className="report-table-container">
                {chart}
            </section>
            <section className="iuc-chart-desc-cont">
                <section className="iuc-chart-desc">
                    <GraphArrowIncrease width="16px" height="16px" />
                    <p>
                        <span className="iuc-chart-desc-hl">{`${getHighestOrLowestNameDisplay(highest.names, itemType)} `}</span>
                        {highestDescription}
                        <span className='iuc-chart-desc-hl'>{` ${highestValueDisplay}.*`}</span>
                    </p>
                </section>
                { itemType !== 'waterLeakage' && <section className="iuc-chart-desc mt-2">
                    <GraphArrowDecrease width="16px" height="16px" />
                    <p>
                        <span className="iuc-chart-desc-hl">{`${getHighestOrLowestNameDisplay(lowest.names, itemType)} `}</span>
                        {lowestDescription}
                        <span className='iuc-chart-desc-hl'>{` ${lowestValueDisplay}.*`}</span>
                    </p>
                </section>}

            </section>
        </>;
    }

    const titleTimeText = getTitleByTime(timeRangeType);

    return (
        <Card className="iuc">
            <h6 className="iuc-title">{`${title}${titleTimeText}`}</h6>

            {body}
        </Card>
    );
});
